import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Crown,
  Shield,
  Building2,
  Check,
  CreditCard,
  Calendar,
  Users,
  Sparkles,
  AlertCircle,
  Loader2,
  UserPlus,
  DollarSign,
  Lock,
} from "lucide-react";
import { useUserDataContext } from "../../context/UserDataContext";
import { billingApi, usersApi } from "../../services/api";
import UpgradeOptions from "../UpgradeOptions";

const PLAN_DISPLAY: Record<
  string,
  { name: string; icon: React.FC<any>; color: string }
> = {
  basic: { name: "Starter", icon: Shield, color: "bg-slate-100 text-slate-600" },
  pro: { name: "Professional", icon: Crown, color: "bg-brand-light text-brand-accent" },
  elite: { name: "Firm", icon: Building2, color: "bg-purple-100 text-purple-600" },
};

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { firmData, canManageBilling } = useUserDataContext();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [cardInfo, setCardInfo] = useState<any>(null);
  const [loadingCard, setLoadingCard] = useState(true);
  const [isOnTrial, setIsOnTrial] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [seatData, setSeatData] = useState<{
    seats: number;
    maxSeats: number | null;
    plan: string;
    isTrial: boolean;
    duration: string | null;
    pricing: { monthly: number; annual: number } | null;
  } | null>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(true);

  // Compute plan info
  const currentPlan = firmData?.subscriptionStatus || "free trial";
  const isTrialActive = firmData?.isOnTrial || false;
  const trialExpiration = firmData?.stripe?.trialExpiration;
  const hasCancelled = firmData?.stripe?.hasCancelled || false;
  const seatCount = seatData?.seats || firmData?.users?.length || 1;

  useEffect(() => {
    setIsOnTrial(isTrialActive);
  }, [isTrialActive]);

  // Fetch card on file + seat data
  useEffect(() => {
    const fetchCard = async () => {
      try {
        const res = await billingApi.getCurrentCard();
        setCardInfo(res.data.card);
      } catch {
        setCardInfo(null);
      } finally {
        setLoadingCard(false);
      }
    };
    const fetchSeats = async () => {
      try {
        const res = await billingApi.getSeats();
        setSeatData(res.data);
      } catch {
        // seat data is optional / non-blocking
      }
    };
    const fetchTeam = async () => {
      try {
        const res = await usersApi.list({ limit: 100 });
        setTeamMembers(res.data?.data || res.data || []);
      } catch {
        // silent
      } finally {
        setLoadingTeam(false);
      }
    };
    fetchSeats();
    fetchTeam();
    if (!isTrialActive) fetchCard();
    else setLoadingCard(false);
  }, [isTrialActive]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await billingApi.cancelSubscription();
      window.location.reload();
    } catch (err) {
      console.error("Failed to cancel:", err);
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    try {
      await billingApi.reactivateSubscription();
      window.location.reload();
    } catch (err) {
      console.error("Failed to reactivate:", err);
    }
  };

  // Mode 0: Restricted view for non-admin users without billing permission
  if (!canManageBilling) {
    const planInfo = PLAN_DISPLAY[currentPlan] || {
      name: currentPlan,
      icon: Shield,
      color: "bg-slate-100 text-slate-600",
    };
    const PlanIcon = planInfo.icon;
    const billingFreq = firmData?.stripe?.subscriptionDuration || "monthly";

    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Subscription & Billing</h1>
          <p className="text-sm text-slate-500 mt-1">View your firm's subscription details</p>
        </div>

        {/* Restricted notice */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-8 text-center mb-6"
        >
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-slate-100 mb-4">
            <Lock className="h-6 w-6 text-slate-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-1">
            Subscription management is restricted
          </h2>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Contact your firm administrator to manage the subscription, change plans, or update billing.
          </p>
        </motion.div>

        {/* Read-only plan info */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl border border-slate-200 p-6"
        >
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${planInfo.color}`}>
              <PlanIcon className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                {isTrialActive ? "Free Trial" : `${planInfo.name} Plan`}
              </h2>
              {isTrialActive && trialExpiration ? (
                <p className="text-sm text-slate-500 mt-0.5">
                  {Math.max(0, Math.ceil((new Date(trialExpiration).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} days remaining
                </p>
              ) : (
                <p className="text-sm text-slate-500 mt-0.5 capitalize">{billingFreq} billing</p>
              )}
              <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-600">
                <Users className="h-4 w-4" />
                <span>{seatCount} user{seatCount !== 1 ? "s" : ""}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Mode 1: Show UpgradeOptions (trial or upgrade flow)
  if (showUpgrade || isTrialActive) {
    return (
      <div>
        {/* Header with back button */}
        {!isTrialActive && (
          <div className="mb-6">
            <button
              onClick={() => setShowUpgrade(false)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm font-medium">Back to Subscription</span>
            </button>
          </div>
        )}
        <UpgradeOptions isOnTrial={isOnTrial} setIsOnTrial={setIsOnTrial} />
      </div>
    );
  }

  // Mode 2: Subscription dashboard (active subscribers)
  const planInfo = PLAN_DISPLAY[currentPlan] || {
    name: currentPlan,
    icon: Shield,
    color: "bg-slate-100 text-slate-600",
  };
  const PlanIcon = planInfo.icon;
  const billingFrequency = firmData?.stripe?.subscriptionDuration || "monthly";

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">
          Subscription & Billing
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage your plan, billing, and payment methods
        </p>
      </div>

      <div className="space-y-6">
        {/* Current Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-6"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div
                className={`h-12 w-12 rounded-xl flex items-center justify-center ${planInfo.color}`}
              >
                <PlanIcon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  {planInfo.name} Plan
                </h2>
                <p className="text-sm text-slate-500 mt-0.5 capitalize">
                  {billingFrequency} billing
                </p>
                <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-600">
                  <Users className="h-4 w-4" />
                  <span>
                    {seatCount} user{seatCount !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUpgrade(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-xl hover:bg-brand-hover transition-colors"
              >
                Change Plan
              </button>
            </div>
          </div>

          {hasCancelled && (
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Your subscription will end at the current billing period
              </p>
            </div>
          )}
        </motion.div>

        {/* Seat-Based Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl border border-slate-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-emerald-50">
                <Users className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  Team Seats
                </h2>
                <p className="text-xs text-slate-500">
                  Per-seat pricing  -  each team member is billed equally
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/settings/users")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand border border-brand/20 rounded-xl hover:bg-brand-light transition-colors"
            >
              <UserPlus className="h-3.5 w-3.5" />
              Manage Team
            </button>
          </div>

          {/* Seat usage bar */}
          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">
                {seatCount} active seat{seatCount !== 1 ? "s" : ""}
              </span>
              {seatData?.maxSeats && (
                <span className="text-xs text-slate-400">
                  {seatData.maxSeats} max on trial
                </span>
              )}
            </div>
            {seatData?.maxSeats && (
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    seatCount / seatData.maxSeats > 0.8
                      ? "bg-amber-500"
                      : "bg-emerald-500"
                  }`}
                  style={{
                    width: `${Math.min((seatCount / seatData.maxSeats) * 100, 100)}%`,
                  }}
                />
              </div>
            )}
          </div>

          {/* Pricing breakdown */}
          {seatData?.pricing && seatData.duration && (
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Per Seat
                  </p>
                  <p className="text-lg font-bold text-slate-800 mt-0.5">
                    ${seatData.duration === "annual"
                      ? seatData.pricing.annual
                      : seatData.pricing.monthly}
                    <span className="text-xs font-normal text-slate-400">/mo</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Seats
                  </p>
                  <p className="text-lg font-bold text-slate-800 mt-0.5">
                    {seatCount}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Monthly Total
                  </p>
                  <p className="text-lg font-bold text-brand mt-0.5">
                    ${(seatData.duration === "annual"
                      ? seatData.pricing.annual
                      : seatData.pricing.monthly) * seatCount}
                    <span className="text-xs font-normal text-slate-400">/mo</span>
                  </p>
                </div>
              </div>
              {seatData.duration === "annual" && (
                <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-200">
                  Billed annually at $
                  {seatData.pricing.annual * seatCount * 12}/yr
                  {" "}(saving ${(seatData.pricing.monthly - seatData.pricing.annual) * seatCount * 12}/yr vs monthly)
                </p>
              )}
            </div>
          )}

          {/* Trial info */}
          {seatData?.isTrial && (
            <p className="text-xs text-slate-400 mt-3">
              During your free trial, you can add up to {seatData.maxSeats} team members.
              Choose a plan to unlock unlimited seats.
            </p>
          )}
        </motion.div>

        {/* Payment Method Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-slate-200 p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-slate-100">
              <CreditCard className="h-5 w-5 text-slate-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">
              Payment Method
            </h2>
          </div>

          {loadingCard ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading payment info...</span>
            </div>
          ) : cardInfo ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                  <span className="text-sm font-mono text-slate-700 capitalize">
                    {cardInfo.brand} **** {cardInfo.last4}
                  </span>
                </div>
                <span className="text-sm text-slate-500">
                  Expires {cardInfo.exp_month}/{cardInfo.exp_year}
                </span>
              </div>
              <button className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                Update Card
              </button>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              No payment method on file
            </p>
          )}
        </motion.div>

        {/* Billing Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-xl border border-slate-200 p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-slate-100">
              <Calendar className="h-5 w-5 text-slate-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">
              Billing Info
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Next Billing Date
              </p>
              <p className="text-sm font-medium text-slate-800 mt-1">
                {firmData?.nextBillDate
                  ? new Date(firmData.nextBillDate).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "long", day: "numeric" }
                    )
                  : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Billing Frequency
              </p>
              <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                {billingFrequency}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Cancel / Reactivate Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-slate-200 p-6"
        >
          {hasCancelled ? (
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">
                  Reactivate Subscription
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Resume your subscription before the current period ends
                </p>
              </div>
              <button
                onClick={handleReactivate}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-xl hover:bg-brand-hover transition-colors"
              >
                Reactivate
              </button>
            </div>
          ) : (
            <div>
              {!showCancelConfirm ? (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      Cancel Subscription
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5">
                      You will retain access until the end of your billing
                      period
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition-colors"
                  >
                    Cancel Subscription
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <h3 className="text-sm font-semibold text-red-700">
                      Are you sure you want to cancel?
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Your subscription will remain active until the end of the
                    current billing period. After that, you will lose access to
                    paid features.
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {cancelling && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {cancelling ? "Cancelling..." : "Confirm Cancellation"}
                    </button>
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      Keep Subscription
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Team Members Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-xl border border-slate-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-brand-light">
                <Users className="h-5 w-5 text-brand" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Team Members</h2>
                <p className="text-xs text-slate-500">
                  {teamMembers.filter((m) => m.status === "active").length} active,{" "}
                  {teamMembers.filter((m) => m.status === "invited").length} invited
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/settings/users")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand border border-brand/20 rounded-xl hover:bg-brand-light transition-colors"
            >
              <UserPlus className="h-3.5 w-3.5" />
              Manage Users
            </button>
          </div>

          {loadingTeam ? (
            <div className="flex items-center gap-2 text-slate-500 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading team...</span>
            </div>
          ) : teamMembers.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No team members found</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {teamMembers.slice(0, 10).map((member) => {
                const statusColors: Record<string, string> = {
                  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
                  invited: "bg-amber-50 text-amber-700 border-amber-200",
                  disabled: "bg-slate-100 text-slate-500 border-slate-200",
                };
                return (
                  <div key={member._id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-brand-light flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-brand">
                          {member.name?.charAt(0)?.toUpperCase() || "?"}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{member.name}</p>
                        <p className="text-xs text-slate-500">{member.email}</p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border capitalize ${
                        statusColors[member.status] || statusColors.active
                      }`}
                    >
                      {member.status}
                    </span>
                  </div>
                );
              })}
              {teamMembers.length > 10 && (
                <div className="pt-3">
                  <button
                    onClick={() => navigate("/settings/users")}
                    className="text-sm font-medium text-brand hover:text-brand-hover transition-colors"
                  >
                    View all {teamMembers.length} members
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
